import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';

const BASE_URL = 'https://mitao-video.example.com';
const FORM_ACTION = 'https://readdy.ai/api/form/d9sls6h4ne17ei0sdjkg';

export default function About() {
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData(form);
      const res = await fetch(FORM_ACTION, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (res.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const errMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          '提交失败，请稍后重试';
        setFormError(errMsg);
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃视频在线',
    url: BASE_URL,
    description: '高清视频免费在线观看平台，海量精选视频内容免费畅享，支持多设备访问。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@mitao-video.example.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蜜桃视频在线',
    url: `${BASE_URL}/about`,
    description: '了解蜜桃视频在线的品牌故事、平台优势和联系方式。',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${BASE_URL}/about` },
    ],
  };

  return (
    <>
      <JsonLd data={organizationSchema} />
      <JsonLd data={aboutPageSchema} />
      <JsonLd data={breadcrumbSchema} />
      <SEOMeta
        title="关于我们"
        description="蜜桃视频在线是领先的高清视频在线观看平台，提供海量精选视频内容免费在线观看。了解我们的品牌故事、平台优势与联系方式。"
        keywords="蜜桃视频在线,关于蜜桃视频,蜜桃视频平台介绍,蜜桃视频联系方式,免费在线视频平台"
        canonicalUrl={`${BASE_URL}/about`}
      />

      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8">
            关于蜜桃视频在线
          </h1>

          {/* Brand Introduction */}
          <section className="mb-12">
            <div className="relative w-full aspect-[21/9] rounded-xl overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20cozy%20living%20room%20with%20large%20TV%20screen%20displaying%20colorful%20video%20content%2C%20warm%20ambient%20lighting%2C%20comfortable%20sofa%2C%20plants%2C%20minimalist%20interior%20design%2C%20soft%20peach%20and%20warm%20neutral%20tones%2C%20cinematic%20atmosphere&width=1400&height=600&seq=mitao-about-hero&orientation=landscape"
                alt="蜜桃视频在线品牌形象"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="space-y-5 text-foreground-700 leading-relaxed">
              <p className="text-lg font-medium text-foreground-900">
                蜜桃视频在线成立于2024年，致力于为广大用户提供高品质、免费的高清视频在线观看服务。
              </p>
              <p>
                作为领先的在线视频内容平台，我们汇聚了海量精选视频资源，涵盖电影、电视剧、综艺节目、动漫、纪录片、短视频等多种类型。无论您是喜欢追剧、看电影，还是想探索新奇有趣的短内容，蜜桃视频在线都能满足您的需求。
              </p>
              <p>
                我们始终坚持"免费、高清、流畅"的服务理念，采用先进的流媒体技术和智能CDN加速方案，确保每一位用户都能享受到流畅无卡顿的观影体验。同时，我们的内容团队每日精选优质视频资源，持续更新平台内容，让您永远有新的精彩内容可看。
              </p>
            </div>
          </section>

          {/* Reputation / Stats */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 font-heading mb-6">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '500万+', label: '月活用户' },
                { value: '10万+', label: '视频资源' },
                { value: '99.9%', label: '服务可用率' },
                { value: '4.9', label: '用户评分' },
              ].map((stat, index) => (
                <div
                  key={index}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 font-heading mb-6">
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '用户至上',
                  desc: '我们始终将用户体验放在第一位，持续优化产品功能，提升服务质量。',
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: '创新驱动',
                  desc: '不断探索和应用新技术，为用户提供更智能、更便捷的视频观看体验。',
                },
                {
                  icon: 'ri-team-line',
                  title: '合作共赢',
                  desc: '与优质内容创作者深度合作，构建健康、可持续的内容生态体系。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '安全可靠',
                  desc: '严格遵守内容安全规范，为用户营造安全、健康的网络视频环境。',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-foreground-950 font-heading mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">
                        电子邮箱
                      </h3>
                      <p className="text-sm text-foreground-600">
                        support@mitao-video.example.com
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">
                        客服热线
                      </h3>
                      <p className="text-sm text-foreground-600">
                        400-888-9999
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">
                        工作时间
                      </h3>
                      <p className="text-sm text-foreground-600">
                        周一至周五 9:00 - 18:00
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">
                        公司地址
                      </h3>
                      <p className="text-sm text-foreground-600">
                        北京市朝阳区科技园区创新路88号
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">
                  发送消息
                </h3>
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>

                  {formSuccess && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm text-center">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      消息已成功发送，我们会尽快回复您！
                    </div>
                  )}
                  {formError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}